import React, { useState, useEffect } from "react";
import { uploadBulkUsers, fetchChildOffices } from "../api";
import Cookie from "js-cookie";
import OfficeMultiSelect from "./OfficeMultiSelect";

const officeCode = Cookie.get("officeCode");

const emptyUser = () => ({
    firstName: "",
    lastName: "",
    email: "",
    jobTitle: "",
    industry: "",
    // LGX-787: offices and admin status are now per-row, not one global dropdown for the whole
    // batch -- default each new row to the admin's own current office so the picker never starts
    // empty; the admin can still add/remove offices per row before sending.
    officeCodes: officeCode ? [officeCode] : [],
    isAdmin: false,
});

const BulkUploadTab = (props) => {
    const [users, setUsers] = useState([emptyUser()]);
    const [numToAdd, setNumToAdd] = useState(1);
    const [loading, setLoading] = useState(false);
    const [availableOffices, setAvailableOffices] = useState([]);
    const token = Cookie.get("authToken");

    useEffect(() => {
        if (!officeCode) return;
        fetchChildOffices(officeCode, token)
            .then((res) => setAvailableOffices(res.data || []))
            .catch((err) => {
                console.error("Failed to load offices:", err.response?.data || err.message);
                setAvailableOffices([]);
            });
    }, [token]);

    const handleField = (idx, field, value) => {
        setUsers(users =>
            users.map((u, i) => (i === idx ? { ...u, [field]: value } : u))
        );
    };

    const handleAddRows = () => {
        setUsers(users => [...users, ...Array(Number(numToAdd)).fill().map(emptyUser)]);
    };

    const handleAddOne = () => setUsers(users => [...users, emptyUser()]);

    const handleDelete = idx => setUsers(users => users.filter((_, i) => i !== idx));

    const canSend = users.length > 0 && users.every(
        u => u.firstName.trim() && u.email.trim() && u.officeCodes.length > 0
    );

    const handleSend = async () => {
        setLoading(true);
        try {
            const res= await uploadBulkUsers(officeCode, users);
            setUsers([emptyUser()]);
            if (props.onSuccess) props.onSuccess(res?.data || "Invites sent!");
            // window.location.href = `?msg=${encodeURIComponent(res?.data || "Invites sent!")}&type=success`;

            // alert("Invites sent!");
        } catch {
            alert("Failed to send invites.");
        }
        setLoading(false);
    };

    const inputClass = "border border-gray-300 dark:border-acs-dark-border rounded px-2 py-1 w-full bg-white dark:bg-acs-dark-surface text-gray-900 dark:text-acs-dark-text placeholder-gray-400 dark:placeholder-acs-dark-subtext";

    return (
        <div className="bg-white dark:bg-acs-dark-surface rounded-lg shadow p-6 border border-gray-200 dark:border-acs-dark-border">
            <div className="flex justify-between items-end mb-4">
                <div className="flex gap-2 items-end">
                    <div>
                        <label className="block text-xs font-semibold mb-1 text-gray-700 dark:text-acs-dark-text">Number to Add</label>
                        <input
                            type="number"
                            min={1}
                            value={numToAdd}
                            onChange={e => setNumToAdd(e.target.value)}
                            className={`${inputClass} w-20`}
                        />
                    </div>
                    <button
                        className="ml-2 px-3 py-1 bg-[#1e2a74] hover:bg-[#18225c] text-white rounded"
                        onClick={handleAddRows}
                    >
                        Add
                    </button>
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="min-w-full text-fluid-sm">
                    <thead>
                    <tr className="bg-gray-50 dark:bg-[#1a1c23] border-b border-gray-200 dark:border-acs-dark-border">
                        <th className="px-2 py-2 text-left font-semibold text-xs text-gray-700 dark:text-acs-dark-text">First Name<span className="text-red-500">*</span></th>
                        <th className="px-2 py-2 text-left font-semibold text-xs text-gray-700 dark:text-acs-dark-text">Last Name</th>
                        <th className="px-2 py-2 text-left font-semibold text-xs text-gray-700 dark:text-acs-dark-text">Email<span className="text-red-500">*</span></th>
                        <th className="px-2 py-2 text-left font-semibold text-xs text-gray-700 dark:text-acs-dark-text">Job Title</th>
                        <th className="px-2 py-2 text-left font-semibold text-xs text-gray-700 dark:text-acs-dark-text">Industry</th>
                        <th className="px-2 py-2 text-left font-semibold text-xs text-gray-700 dark:text-acs-dark-text">Offices<span className="text-red-500">*</span></th>
                        <th className="px-2 py-2 text-center font-semibold text-xs text-gray-700 dark:text-acs-dark-text">Admin</th>
                        <th className="px-2 py-2"></th>
                    </tr>
                    </thead>
                    <tbody>
                    {users.map((u, idx) => (
                        <tr key={idx} className="border-b border-gray-100 dark:border-acs-dark-border">
                            <td className="px-2 py-1">
                                <input
                                    className={inputClass}
                                    value={u.firstName}
                                    onChange={e => handleField(idx, "firstName", e.target.value)}
                                    required
                                />
                            </td>
                            <td className="px-2 py-1">
                                <input
                                    className={inputClass}
                                    value={u.lastName}
                                    onChange={e => handleField(idx, "lastName", e.target.value)}
                                />
                            </td>
                            <td className="px-2 py-1">
                                <input
                                    className={inputClass}
                                    value={u.email}
                                    onChange={e => handleField(idx, "email", e.target.value)}
                                    required
                                />
                            </td>
                            <td className="px-2 py-1">
                                <input
                                    className={inputClass}
                                    value={u.jobTitle}
                                    onChange={e => handleField(idx, "jobTitle", e.target.value)}
                                />
                            </td>
                            <td className="px-2 py-1">
                                <input
                                    className={inputClass}
                                    value={u.industry}
                                    onChange={e => handleField(idx, "industry", e.target.value)}
                                />
                            </td>
                            <td className="px-2 py-1">
                                <OfficeMultiSelect
                                    availableOffices={availableOffices}
                                    selectedCodes={u.officeCodes}
                                    onChange={(codes) => handleField(idx, "officeCodes", codes)}
                                />
                            </td>
                            <td className="px-2 py-1 text-center">
                                <input
                                    type="checkbox"
                                    checked={u.isAdmin}
                                    onChange={e => handleField(idx, "isAdmin", e.target.checked)}
                                    className="accent-[#223688] w-4 h-4 cursor-pointer"
                                    title="Make this user an admin"
                                />
                            </td>
                            <td className="px-2 py-1">
                                <button
                                    className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                                    onClick={() => handleDelete(idx)}
                                    title="Delete"
                                >
                                    &#10005;
                                </button>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
            <div className="flex items-center justify-between mt-4">
                <div>
                    <button
                        className="px-3 py-1 bg-[#1e2a74] hover:bg-[#18225c] text-white rounded"
                        onClick={handleAddOne}
                    >
                        + Add Row
                    </button>
                </div>
                <div className="text-gray-700 dark:text-acs-dark-subtext text-sm">
                    Total fields: <span className="font-bold">{users.length}</span>
                </div>
                <div>
                    <button
                        className={`px-4 py-2 rounded text-white ${canSend ? "bg-[#1e2a74] hover:bg-[#18225c]" : "bg-gray-300 dark:bg-acs-dark-border cursor-not-allowed"}`}
                        disabled={!canSend || loading}
                        onClick={handleSend}
                    >
                        {loading ? "Sending..." : "Send Invite"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BulkUploadTab;
